import { rtkApi } from '@/shared/api/rtkApi';

export interface UserProfilePayload {
  id: number;
  fullName: string | null;
  lastName: string | null;
  firstName: string | null;
  patronymic: string | null;
  email: string | null;
  phoneNumber: string | null;
  region: string | null;
  nickname: string | null;
  qualification: string | null;
  vkLink: string | null;
  rate: string | null;
  status: string | null;
  birthDate: string | null;
  role: string;
  dateAuth: string | null;
  clubName?: string | null;
  club?: { name?: string | null } | null;
  isActive?: boolean;
  blockReason?: string | null;
}

export interface ClubAccountPayload {
  id: number;
  login: string | null;
  lastName?: string | null;
  firstName?: string | null;
  patronymic?: string | null;
  role: string;
  hardwareId: string | null;
  dateAuth: string | null;
}

export interface ClubPayload {
  id: number;
  name: string | null;
  region: string | null;
  isActive: boolean;
  balanceTechnocoins: number;
  balanceLimit: number | null;
  balanceLimitEnabled?: boolean;
  loginPrefix: string | null;
  timeBalanceMinutes?: number | null;
  unlimitedUntil?: string | null;
  blockReason?: string | null;
}

export type ProfileApiResponse =
  | { accountType: 'user'; user: UserProfilePayload }
  | {
      accountType: 'club_account';
      clubAccount: ClubAccountPayload;
      club: ClubPayload;
    };

export interface Profile {
  id?: number;
  name?: string;
  role: string;
  accountType: 'user' | 'club_account';
  lastName?: string | null;
  firstName?: string | null;
  patronymic?: string | null;
  fullName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  region?: string | null;
  nickname?: string | null;
  qualification?: string | null;
  vkLink?: string | null;
  rate?: string | null;
  status?: string | null;
  birthDate?: string | null;
  dateAuth?: string | null;
  clubName?: string | null;
  clubRegion?: string | null;
  isActive?: boolean;
  balanceTechnocoins?: number;
  balanceLimit?: number | null;
  balanceLimitEnabled?: boolean;
  loginPrefix?: string | null;
  login?: string | null;
  hardwareId?: string | null;
  timeBalanceMinutes?: number | null;
  unlimitedUntil?: string | null;
  blockReason?: string | null;
}

export const mapProfileResponseToProfile = (response: ProfileApiResponse): Profile => {
  if (response.accountType === 'user') {
    const user = response.user;
    return {
      id: user.id,
      fullName: user.fullName ?? null,
      lastName: user.lastName ?? null,
      firstName: user.firstName ?? null,
      patronymic: user.patronymic ?? null,
      email: user.email ?? null,
      phoneNumber: user.phoneNumber ?? null,
      region: user.region ?? null,
      nickname: user.nickname ?? null,
      qualification: user.qualification ?? null,
      vkLink: user.vkLink ?? null,
      rate: user.rate ?? null,
      status: user.status ?? null,
      birthDate: user.birthDate ?? null,
      role: user.role,
      dateAuth: user.dateAuth ?? null,
      accountType: 'user',
      clubName: user.clubName ?? user.club?.name ?? null,
      clubRegion: null,
      isActive: Boolean(user.isActive),
      balanceTechnocoins: 0,
      balanceLimit: null,
      loginPrefix: null,
      hardwareId: null,
      timeBalanceMinutes: null,
      unlimitedUntil: null,
      blockReason: user.blockReason ?? null,
    };
  }

  const { clubAccount, club } = response;
  return {
    id: clubAccount.id,
    name: clubAccount.login ?? '',
    login: clubAccount.login ?? null,
    role: clubAccount.role,
    lastName: clubAccount.lastName ?? null,
    firstName: clubAccount.firstName ?? null,
    patronymic: clubAccount.patronymic ?? null,
    fullName: [clubAccount.lastName, clubAccount.firstName, clubAccount.patronymic].filter(Boolean).join(' ') || null,
    email: null,
    phoneNumber: null,
    region: null,
    nickname: null,
    qualification: null,
    vkLink: null,
    rate: null,
    status: null,
    birthDate: null,
    dateAuth: clubAccount.dateAuth ?? null,
    accountType: 'club_account',
    clubName: club.name ?? null,
    clubRegion: club.region ?? null,
    isActive: club.isActive,
    balanceTechnocoins: club.balanceTechnocoins,
    balanceLimit: club.balanceLimit ?? null,
    balanceLimitEnabled: club.balanceLimitEnabled ?? false,
    loginPrefix: club.loginPrefix ?? null,
    hardwareId: clubAccount.hardwareId ?? null,
    timeBalanceMinutes: club.timeBalanceMinutes ?? null,
    unlimitedUntil: club.unlimitedUntil ?? null,
    blockReason: club.blockReason ?? null,
  };
};

export interface ProfileStatisticsExercise {
  exerciseType: number;
  rounds: number;
  percentage: number;
  kills: number;
  deaths: number;
  killDeathRatio: number;
  coverHits: number;
  stepOuts: number;
  buttonCaptures: number;
  revives: number;
  gameTime: number;
  targetsHit: number;
  targetsMissed: number;
  roundsWon: number;
  roundsLost: number;
  winRate: number;
}

export interface ProfileStatistics {
  totalKills: number;
  totalDeaths: number;
  killDeathRatio: number;
  roundsWon: number;
  roundsLost: number;
  winRate: number;
  secondsPlayed: number;
  serversCreated: number;
  exercises: ProfileStatisticsExercise[];
}

export interface ProfileStatisticsParams {
  startAt?: string;
  endAt?: string;
  timezone?: string;
}

export interface ClubServersSummary {
  totalSeconds: number;
}

export interface OwnerClubAccount {
  id: number;
  login: string;
  lastName?: string | null;
  firstName?: string | null;
  patronymic?: string | null;
  role: string;
  hardwareId: string | null;
  isActive: boolean;
}

export interface ClubAccountsResponse {
  accounts: OwnerClubAccount[];
}

export interface GameSessionsParams {
  startAt?: string;
  endAt?: string;
  timezone?: string;
  creatorLogins?: string[];
}

export interface GameSessionsSummaryResponse {
  totalSessions: number;
  totalMinutesSpent: number;
  totalTechnocoinsSpent: number;
  percentAuthorized: number;
}

export interface GameSessionItem {
  id: number;
  type: 'match' | 'training';
  composition: 'only_our_club' | 'our_players_in_other_session' | 'other_clubs_players';
  createdAt: string;
  name: string | null;
  creatorName: string | null;
  playersCount: number;
  technocoinsSpent: number;
  durationSeconds: number;
  roles?: Record<string, { count: number; technocoinsSpent: number }> | null;
}

export interface GameSessionsResponse {
  items: GameSessionItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface GetGameSessionsParams extends GameSessionsParams {
  limit?: number;
  offset?: number;
}

export interface ProfileGameSessionItem {
  id: number;
  type: 'match' | 'training';
  createdAt: string;
  name: string | null;
  clubName: string | null;
  playersCount: number;
  durationSeconds: number;
  stats: {
    kills: number;
    deaths: number;
  };
}

export interface ProfileGameSessionsResponse {
  items: ProfileGameSessionItem[];
  total: number;
  offset: number;
  limit: number;
}

export type GameBuildApp = 'admin' | 'vr';
export type GameBuildPlatform = 'windows' | 'android' | 'vr';

export interface ClubGameBuildLatestLinks {
  windows: string | null;
  android: string | null;
  vr: string | null;
}

export interface ClubGameBuildLatestResponse {
  app: GameBuildApp;
  version: string;
  links?: ClubGameBuildLatestLinks;
}

export interface LinkingCodeResponse {
  code: string | null;
  expiresInSeconds: number;
  linkedClubAccount?: {
    id: number;
    login: string | null;
  };
}

export type TeamApplicationStatus = 'moderation' | 'confirmed' | 'rejected' | 'canceled';

export interface TeamApplication {
  id: number;
  name: string;
  region: string;
  status: TeamApplicationStatus;
  isMember: boolean;
  rejectReason: string | null;
  logo: {
    path: string | null;
    url: string | null;
  };
  createdAt: string | null;
}

export interface GetTeamApplicationResponse {
  application: TeamApplication | null;
}

export interface CreateTeamApplicationResponse {
  application: TeamApplication;
}

export interface EditMyTeamResponse {
  application: TeamApplication;
}

export type TeamMemberRole = 'captain' | 'member';

export interface TeamMemberItem {
  id: number;
  user: {
    id: number;
    name: string | null;
    fullName: string | null;
    nickname: string | null;
  };
  role: TeamMemberRole;
  joinedAt: string;
}

export interface TeamMembersResponse {
  items: TeamMemberItem[];
}

export type TeamInvitationStatus = 'pending' | 'accepted' | 'rejected' | 'expired' | 'canceled';

export interface IncomingTeamInvitationItem {
  id: number;
  team: {
    id: number;
    name: string;
    region: string;
  };
  status: TeamInvitationStatus;
  expiresAt: string;
}

export interface IncomingTeamInvitationsResponse {
  items: IncomingTeamInvitationItem[];
}

export interface OutgoingTeamInvitationItem {
  id: number;
  user: {
    id: number;
    name: string | null;
    fullName: string | null;
    nickname: string | null;
  };
  status: TeamInvitationStatus;
  expiresAt: string;
  updatedAt: string | null;
}

export interface OutgoingTeamInvitationsResponse {
  items: OutgoingTeamInvitationItem[];
}

export interface CreateTeamInvitationArgs {
  loginOrPhone: string;
}

export type TeamInviteCandidateStatus = 'free' | 'in_team';

export interface TeamInviteCandidateItem {
  id: number;
  login: string | null;
  fullName: string | null;
  nickname: string | null;
  status: TeamInviteCandidateStatus;
  phoneNumber: string | null;
}

export interface TeamInviteCandidatesResponse {
  items: TeamInviteCandidateItem[];
}

export interface GetLinkingCodeParams {
  mode?: 'polling';
}

export interface UpdateProfileData {
  lastName?: string | null;
  firstName?: string | null;
  patronymic?: string | null;
  region?: string | null;
  nickname?: string | null;
  vkLink?: string | null;
  birthDate?: string | null;
}

export interface ClubOperationItem {
  id: number;
  type: 'deposit' | 'withdrawal';
  reason: string | null;
  technocoins: number;
  amountRubles: number | null;
  editorId: number | null;
  editor: { id: number; fullName: string | null } | null;
  createdAt: string;
}

export interface ClubOperationsResponse {
  items: ClubOperationItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface GetClubOperationsParams {
  limit?: number;
  offset?: number;
  from?: string;
  to?: string;
}

export interface ExportClubOperationsParams {
  from?: string;
  to?: string;
}

export interface ClubTariffItem {
  key: string;
  label: string;
  tariff: number;
}

export interface ClubTariffsResponse {
  items: ClubTariffItem[];
}

export const profileApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getProfileData: build.query<Profile, void>({
      query: () => `/v1/portal/profile/info`,
      transformResponse: (response: ProfileApiResponse) => mapProfileResponseToProfile(response),
      providesTags: ['Profile'],
    }),
    getTeamApplication: build.query<GetTeamApplicationResponse, void>({
      query: () => ({
        url: '/v1/portal/profile/team-application',
      }),
      providesTags: ['TeamApplication'],
    }),
    createTeamApplication: build.mutation<CreateTeamApplicationResponse, FormData>({
      query: (formData) => ({
        url: '/v1/portal/profile/team-application',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    cancelTeamApplication: build.mutation<void, void>({
      query: () => ({
        url: '/v1/portal/profile/team-application',
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    logout: build.mutation<void, void>({
      query: () => ({
        url: '/user/logout',
        method: 'POST',
      }),
    }),
    editMyTeam: build.mutation<EditMyTeamResponse, FormData>({
      query: (formData) => ({
        url: '/v1/portal/profile/team/edit',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    getTeamMembers: build.query<TeamMembersResponse, void>({
      query: () => ({
        url: '/v1/portal/profile/team/members',
      }),
      providesTags: ['TeamApplication'],
    }),
    removeTeamMember: build.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/v1/portal/profile/team/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    transferTeamCaptain: build.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/v1/portal/profile/team/members/${userId}/transfer-captain`,
        method: 'POST',
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    leaveTeam: build.mutation<void, void>({
      query: () => ({
        url: '/v1/portal/profile/team/members/leave',
        method: 'POST',
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    getIncomingTeamInvitations: build.query<IncomingTeamInvitationsResponse, void>({
      query: () => ({
        url: '/v1/portal/profile/team/invitations/incoming',
      }),
      providesTags: ['TeamApplication'],
    }),
    getOutgoingTeamInvitations: build.query<OutgoingTeamInvitationsResponse, void>({
      query: () => ({
        url: '/v1/portal/profile/team/invitations/outgoing',
      }),
      providesTags: ['TeamApplication'],
    }),
    createTeamInvitation: build.mutation<void, CreateTeamInvitationArgs>({
      query: ({ loginOrPhone }) => ({
        url: '/v1/portal/profile/team/invitations',
        method: 'POST',
        body: { loginOrPhone },
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    getTeamInviteCandidates: build.query<TeamInviteCandidatesResponse, { q: string }>({
      query: ({ q }) => ({
        url: '/v1/portal/profile/team/invitations/candidates',
        params: { q },
      }),
    }),
    cancelOutgoingTeamInvitation: build.mutation<void, { inviteId: number }>({
      query: ({ inviteId }) => ({
        url: `/v1/portal/profile/team/invitations/${inviteId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    acceptIncomingTeamInvitation: build.mutation<void, { inviteId: number }>({
      query: ({ inviteId }) => ({
        url: `/v1/portal/profile/team/invitations/${inviteId}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    rejectIncomingTeamInvitation: build.mutation<void, { inviteId: number }>({
      query: ({ inviteId }) => ({
        url: `/v1/portal/profile/team/invitations/${inviteId}/reject`,
        method: 'POST',
      }),
      invalidatesTags: ['TeamApplication'],
    }),
    getLinkingCode: build.query<LinkingCodeResponse, GetLinkingCodeParams | void>({
      query: (params) => {
        const queryParams = params as GetLinkingCodeParams | undefined;
        return {
          url: '/v1/portal/profile/linking-code',
          params: queryParams as Record<string, any> | undefined,
        };
      },
      providesTags: ['Profile'],
    }),
    refreshLinkingCode: build.mutation<LinkingCodeResponse, void>({
      query: () => ({
        url: '/v1/portal/profile/linking-code',
        method: 'POST',
      }),
    }),
    unlinkSelf: build.mutation<void, void>({
      query: () => ({
        url: '/v1/portal/profile/unlink-self',
        method: 'DELETE',
      }),
    }),
    getProfileStatistics: build.query<ProfileStatistics, ProfileStatisticsParams | void>({
      query: (params) => {
        const fetchArgs: { url: string; params?: Record<string, any> } = {
          url: `/v1/portal/profile/statistics`,
        };
        if (params && typeof params === 'object') {
          fetchArgs.params = params as Record<string, any>;
        }
        return fetchArgs;
      },
      providesTags: ['Profile'],
    }),
    getClubPlayerSessionsSummary: build.query<ClubServersSummary, ProfileStatisticsParams | void>({
      query: (params) => {
        const fetchArgs: { url: string; params?: Record<string, any> } = {
          url: `/v1/portal/club/player-sessions-summary`,
        };
        if (params && typeof params === 'object') {
          fetchArgs.params = params as Record<string, any>;
        }
        return fetchArgs;
      },
    }),
    getClubAccounts: build.query<ClubAccountsResponse, void>({
      query: () => `/v1/portal/club/accounts`,
    }),
    getClubGameSessionsSummary: build.query<GameSessionsSummaryResponse, GameSessionsParams | void>({
      query: (params) => {
        const fetchArgs: { url: string; params?: Record<string, any> } = {
          url: `/v1/portal/club/game-sessions/summary`,
        };
        if (params && typeof params === 'object') {
          fetchArgs.params = params as Record<string, any>;
        }
        return fetchArgs;
      },
    }),
    getClubGameSessions: build.query<GameSessionsResponse, GetGameSessionsParams | void>({
      query: (params) => {
        const fetchArgs: { url: string; params?: Record<string, any> } = {
          url: `/v1/portal/club/game-sessions`,
        };
        if (params && typeof params === 'object') {
          fetchArgs.params = params as Record<string, any>;
        }
        return fetchArgs;
      },
    }),

    getClubGameBuildLatest: build.query<ClubGameBuildLatestResponse, { app: GameBuildApp; expiresMinutes?: number }>({
      query: ({ app, expiresMinutes }) => {
        const params: Record<string, any> = { app };
        if (typeof expiresMinutes === 'number') {
          params.expiresMinutes = expiresMinutes;
        }

        return {
          url: `/v1/portal/club/game-builds/latest`,
          params,
        };
      },
    }),
    changeClubAccountsPassword: build.mutation<{ status: string }, { password: string }>({
      query: ({ password }) => ({
        url: `/v1/portal/club/accounts/password`,
        method: 'PATCH',
        body: { password },
      }),
    }),
    updateProfileData: build.mutation<void, UpdateProfileData>({
      query: (data) => ({
        url: `/v1/portal/profile/info`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Profile'],
    }),
    updateClubProfileData: build.mutation<void, UpdateProfileData>({
      query: (data) => ({
        url: `/v1/portal/club/profile`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Profile'],
    }),
    updateClubLimit: build.mutation<void, { limit: number; isEnabled: boolean }>({
      query: (data) => ({
        url: `/v1/portal/club/limit`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ limit, isEnabled }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profileApi.util.updateQueryData('getProfileData', undefined, (draft) => {
            if (draft) {
              draft.balanceLimit = limit;
              draft.balanceLimitEnabled = isEnabled;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getClubOperations: build.query<ClubOperationsResponse, GetClubOperationsParams | void>({
      query: (params) => {
        const fetchArgs: { url: string; params?: Record<string, any> } = {
          url: `/v1/portal/club/operations`,
        };
        if (params && typeof params === 'object') {
          fetchArgs.params = params as Record<string, any>;
        }
        return fetchArgs;
      },
    }),
    getClubTariffs: build.query<ClubTariffsResponse, void>({
      query: () => ({
        url: `/v1/portal/club/tariffs`,
      }),
    }),
    exportClubOperations: build.mutation<Blob, ExportClubOperationsParams | void>({
      query: (params) => {
        const fetchArgs: { url: string; params?: Record<string, any>; responseHandler: any } = {
          url: `/v1/portal/club/operations/export`,
          responseHandler: (response: any) => response.blob(),
        };
        if (params && typeof params === 'object') {
          fetchArgs.params = params as Record<string, any>;
        }
        return fetchArgs;
      },
    }),
    getProfileGameSessions: build.query<ProfileGameSessionsResponse, GetGameSessionsParams | void>({
      query: (params) => {
        const fetchArgs: { url: string; params?: Record<string, any> } = {
          url: `/v1/portal/profile/game-sessions`,
        };
        if (params && typeof params === 'object') {
          fetchArgs.params = params as Record<string, any>;
        }
        return fetchArgs;
      },
    }),
  }),
});

export const setProfileCache = (profile: Profile) =>
  (profileApi.util.upsertQueryData as any)('getProfileData', undefined, profile);

export const {
  useGetProfileDataQuery,
  useGetProfileStatisticsQuery,
  useGetClubPlayerSessionsSummaryQuery,
  useGetClubAccountsQuery,
  useGetClubGameSessionsSummaryQuery,
  useGetClubGameSessionsQuery,
  useGetClubGameBuildLatestQuery,
  useLazyGetClubGameBuildLatestQuery,
  useChangeClubAccountsPasswordMutation,
  useUpdateProfileDataMutation,
  useUpdateClubProfileDataMutation,
  useUpdateClubLimitMutation,
  useGetTeamApplicationQuery,
  useCreateTeamApplicationMutation,
  useCancelTeamApplicationMutation,
  useEditMyTeamMutation,
  useGetTeamMembersQuery,
  useRemoveTeamMemberMutation,
  useTransferTeamCaptainMutation,
  useLeaveTeamMutation,
  useGetIncomingTeamInvitationsQuery,
  useGetOutgoingTeamInvitationsQuery,
  useCreateTeamInvitationMutation,
  useGetTeamInviteCandidatesQuery,
  useCancelOutgoingTeamInvitationMutation,
  useAcceptIncomingTeamInvitationMutation,
  useRejectIncomingTeamInvitationMutation,
  useGetLinkingCodeQuery,
  useRefreshLinkingCodeMutation,
  useUnlinkSelfMutation,
  useGetClubOperationsQuery,
  useExportClubOperationsMutation,
  useGetClubTariffsQuery,
  useGetProfileGameSessionsQuery,
  useLogoutMutation,
} = profileApi;
